/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/settingsHelpers.ts (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing Agent settings.

────────────────────────────────────────────────────────────────*/

import { FormState } from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';

/**
 * Build settings object from form state
 */
export function buildSettingsFromFormState(formState: FormState): Record<string, unknown> {
  const settings: Record<string, unknown> = {};

  settings.state_mode = formState.stateMode;
  settings.max_no_answer = Number(formState.maxNoAnswer) || 3;
  settings.busy_delay_time = Number(formState.busyDelayTime) || 90;
  settings.reject_delay_time = Number(formState.rejectDelayTime) || 90;
  settings.no_answer_delay_time = Number(formState.noAnswerDelayTime) || 30;
  settings.contact_timeout = Number(formState.contactTimeout) || 20;
  settings.wrap_up_time = Number(formState.wrapUpTime) || 10;
  settings.agent_type = formState.agentType;
  settings.max_calls = Number(formState.maxConcurrentCalls) || 1;
  settings.record_calls = formState.recordCalls;

  return settings;
}

/**
 * Extract extra settings (unknown keys) from settings object
 */
export function extractExtraSettings(settings: Record<string, unknown>): Record<string, unknown> {
  const extraSettings: Record<string, unknown> = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!KNOWN_SETTINGS_KEYS.has(key)) {
      extraSettings[key] = value;
    }
  });
  return extraSettings;
}

/**
 * Get string setting with fallback
 */
export function getStringSetting(settings: Record<string, unknown>, key: string, fallback = ''): string {
  const value = settings[key];
  return typeof value === 'string' ? value : fallback;
}

/**
 * Get boolean setting with fallback
 */
export function getBooleanSetting(settings: Record<string, unknown>, key: string, fallback = false): boolean {
  const value = settings[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  if (typeof value === 'number') return value === 1;
  return fallback;
}

/**
 * Get number setting with fallback
 */
export function getNumberSetting(settings: Record<string, unknown>, key: string, fallback = 0): number {
  const value = settings[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

